import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Mail, Phone } from "lucide-react";
import { getCachedCustomer } from "@/lib/customerAuth";

// Simple in-app profile card. Read-only for now — shows the identity linked
// to the current session (silently created at checkout OR via OTP/Google).
export const ProfileDialog = ({ open, onClose }) => {
  const customer = getCachedCustomer();
  if (!open) return null;
  const initial =
    (customer?.name && customer.name.trim().charAt(0).toUpperCase()) ||
    (customer?.email && customer.email.charAt(0).toUpperCase()) ||
    "U";

  return (
    <AnimatePresence>
      <motion.div
        key="profile-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
        data-testid="profile-dialog"
      >
        <motion.div
          key="profile-card"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_24px_64px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
            <div className="flex items-center gap-2">
              <User size={16} strokeWidth={1.8} className="text-[#064E3B]" />
              <h2 className="font-display text-lg text-[#0B0C10]">Your profile</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="profile-dialog-close"
              className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"
              aria-label="close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#022C22] text-[#E0B53C] flex items-center justify-center font-bold text-2xl overflow-hidden">
                {customer?.picture ? (
                  <img
                    src={customer.picture}
                    alt={customer.name || "you"}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  initial
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="font-display text-xl text-[#0B0C10] truncate"
                  data-testid="profile-name"
                >
                  {customer?.name || "MastiBazaar shopper"}
                </p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-400 mt-1">
                  Signed in via {customer?.provider || "MastiBazaar"}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Row
                icon={<Mail size={14} strokeWidth={1.8} />}
                label="Email"
                value={customer?.email || "—"}
                testId="profile-email"
              />
              <Row
                icon={<Phone size={14} strokeWidth={1.8} />}
                label="Phone"
                value={customer?.phone ? `+91 ${customer.phone}` : "—"}
                testId="profile-phone"
              />
            </div>

            <p className="text-[11px] text-neutral-500 leading-relaxed pt-2 border-t border-black/10">
              Your account was created automatically when you placed your first
              order. Every purchase gets linked here so you can track orders
              and file returns in a single tap.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Row = ({ icon, label, value, testId }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-neutral-50 px-4 py-3">
    <span className="text-neutral-500 mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
        {label}
      </p>
      <p
        className="text-sm text-[#0B0C10] font-medium truncate"
        data-testid={testId}
      >
        {value}
      </p>
    </div>
  </div>
);
